import { useState } from "react";
import { MdContentCopy, MdCheck } from "react-icons/md";

export const BUTTON = {
  primary: "rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50",
  neutral: "rounded-lg bg-lightPrimary px-3 py-2 text-xs font-semibold text-navy-700 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600",
  outline: "rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-navy-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-transparent dark:text-white dark:hover:bg-navy-700",
  success: "rounded-lg bg-green-50 px-3 py-2 text-xs font-semibold text-green-600 transition hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-green-400/10 dark:text-green-300",
  danger: "rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-500 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-red-400/10 dark:text-red-300",
  warning: "rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-600 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amber-400/10 dark:text-amber-300",
  info: "rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-600 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-400/10 dark:text-blue-300",
};

export const FIELD_INPUT =
  "h-9 w-full rounded-md border border-border bg-transparent px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-[3px] focus:ring-ring/50 dark:bg-input/30 placeholder:text-muted-foreground";

export function DetailGrid({ children, className = "" }) {
  return <dl className={`grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-3 ${className}`}>{children}</dl>;
}

export function DetailField({ label, children, span = false, mono = false }) {
  const empty = children === null || children === undefined || children === "";
  return (
    <div className={`min-w-0 ${span ? "sm:col-span-2 xl:col-span-3" : ""}`}>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 min-w-0 break-words text-sm text-navy-700 dark:text-white ${mono ? "font-mono text-xs" : ""}`}>
        {empty ? <span className="text-gray-400">—</span> : children}
      </dd>
    </div>
  );
}

export function DetailSection({ title, description, children, className = "" }) {
  return (
    <section className={`mt-4 rounded-xl border border-gray-100 bg-white p-4 dark:border-white/10 dark:bg-navy-800 ${className}`}>
      {title && <p className="text-sm font-semibold text-navy-700 dark:text-white">{title}</p>}
      {description && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>}
      <div className={title || description ? "mt-3" : ""}>{children}</div>
    </section>
  );
}

export function DetailActions({ children, label = "Actions" }) {
  return (
    <div className="mt-4 border-t border-gray-100 pt-4 dark:border-white/10">
      <p className="mb-2 text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function IdValue({ value, short = false }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-gray-400">—</span>;
  const text = String(value);

  async function copy(e) {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  }

  return (
    <span className="inline-flex max-w-full items-center gap-1 align-middle">
      <code className="truncate font-mono text-xs text-navy-700 dark:text-white" title={text}>
        {short && text.length > 14 ? `${text.slice(0, 12)}…` : text}
      </code>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy"}
        title={copied ? "Copied" : "Copy"}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:text-brand-500"
      >
        {copied ? <MdCheck className="text-sm text-green-500" /> : <MdContentCopy className="text-xs" />}
      </button>
    </span>
  );
}

export function JsonValue({ value }) {
  if (value === null || value === undefined) return <span className="text-gray-400">—</span>;
  let text;
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    text = JSON.stringify(parsed, null, 2);
  } catch {
    text = String(value);
  }
  return (
    <pre className="max-h-64 overflow-auto rounded-lg bg-navy-900 p-3 font-mono text-xs leading-relaxed text-gray-100">
      {text}
    </pre>
  );
}

export function InlineError({ message }) {
  if (!message) return null;
  return <p className="mt-2 text-xs font-medium text-red-500">{message}</p>;
}
